import React, { useEffect, useState } from "react";
import {
  adminLogin,
  adminVerify,
  setToken,
  listTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  listRules,
  updateRule,
  getBracket,
  generateBracket,
  resetBracket,
  setMatchWinner,
  undoMatchWinner,
} from "../lib/api";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Plus,
  Trash2,
  Save,
  Loader2,
  Trophy,
  Users,
  Shield,
  Swords,
  RefreshCw,
  Undo2,
  Check,
} from "lucide-react";

const TABS = [
  { key: "teams", label: "Teams", icon: Users },
  { key: "standings", label: "Standings", icon: Trophy },
  { key: "rules", label: "Rules", icon: Shield },
  { key: "bracket", label: "Bracket", icon: Swords },
];

function LoginForm({ onLoggedIn }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(pw);
      toast.success("Welcome, admin");
      onLoggedIn();
    } catch (err) {
      toast.error("Wrong password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-24" data-testid="admin-login">
      <div className="glass-card rounded-xl p-8">
        <div className="h-12 w-12 rounded-lg bg-[#FF5E00]/15 flex items-center justify-center text-[#FF5E00] mb-6">
          <Lock className="w-6 h-6" />
        </div>
        <h1 className="font-display uppercase text-3xl font-black tracking-tight mb-2">
          Admin Access
        </h1>
        <p className="text-white/60 text-sm mb-6">
          Enter the admin password to manage teams, rules, and the bracket.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Admin password"
            className="w-full bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-4 py-3 focus:outline-none focus:border-[#FF5E00] focus:ring-1 focus:ring-[#FF5E00] transition"
            data-testid="admin-password-input"
            autoFocus
          />
          <button
            type="submit"
            disabled={busy}
            className="w-full font-display uppercase tracking-[0.18em] text-sm font-bold px-7 py-3.5 rounded-md bg-[#FF5E00] text-black hover:bg-[#ff7a33] transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function TeamsTab() {
  const [teams, setTeams] = useState([]);
  const [draft, setDraft] = useState({ name: "", tag: "", logo: "", seeding_points: 0 });

  const refresh = () => listTeams().then(setTeams);
  useEffect(() => {
    refresh();
  }, []);

  const add = async () => {
    if (!draft.name.trim()) return toast.error("Name required");
    try {
      await createTeam({
        name: draft.name.trim(),
        tag: draft.tag.trim(),
        logo: draft.logo.trim(),
        seeding_points: Number(draft.seeding_points) || 0,
      });
      setDraft({ name: "", tag: "", logo: "", seeding_points: 0 });
      toast.success("Team added");
      refresh();
    } catch (e) {
      toast.error("Failed to add team");
    }
  };

  const save = async (t) => {
    try {
      await updateTeam(t.id, {
        name: t.name,
        tag: t.tag,
        logo: t.logo,
        seeding_points: Number(t.seeding_points) || 0,
      });
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete team?")) return;
    await deleteTeam(id);
    toast.success("Deleted");
    refresh();
  };

  const upd = (id, key, val) =>
    setTeams(teams.map((t) => (t.id === id ? { ...t, [key]: val } : t)));

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-xl p-6">
        <h3 className="font-display uppercase text-lg font-bold mb-4 text-[#FF5E00]">
          Add Team
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input
            placeholder="Name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-3 py-2 focus:outline-none focus:border-[#FF5E00]"
            data-testid="add-team-name"
          />
          <input
            placeholder="Tag"
            value={draft.tag}
            onChange={(e) => setDraft({ ...draft, tag: e.target.value })}
            className="bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-3 py-2 focus:outline-none focus:border-[#FF5E00]"
            data-testid="add-team-tag"
          />
          <input
            placeholder="Logo URL"
            value={draft.logo}
            onChange={(e) => setDraft({ ...draft, logo: e.target.value })}
            className="bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-3 py-2 md:col-span-2 focus:outline-none focus:border-[#FF5E00]"
            data-testid="add-team-logo"
          />
          <input
            type="number"
            placeholder="Pts"
            value={draft.seeding_points}
            onChange={(e) => setDraft({ ...draft, seeding_points: e.target.value })}
            className="bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-3 py-2 focus:outline-none focus:border-[#FF5E00]"
            data-testid="add-team-points"
          />
        </div>
        <button
          onClick={add}
          className="mt-4 inline-flex items-center gap-2 font-display uppercase tracking-wider text-xs font-bold px-5 py-2.5 rounded-md bg-[#FF5E00] text-black hover:bg-[#ff7a33]"
          data-testid="add-team-btn"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      <div className="space-y-3">
        {teams.map((t) => (
          <div
            key={t.id}
            className="glass-card rounded-xl p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_2fr_100px_auto] gap-3 items-center"
            data-testid={`admin-team-row-${t.id}`}
          >
            <input
              value={t.name}
              onChange={(e) => upd(t.id, "name", e.target.value)}
              className="bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-3 py-2"
            />
            <input
              value={t.tag || ""}
              onChange={(e) => upd(t.id, "tag", e.target.value)}
              className="bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-3 py-2"
            />
            <input
              value={t.logo || ""}
              onChange={(e) => upd(t.id, "logo", e.target.value)}
              className="bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-3 py-2"
            />
            <input
              type="number"
              value={t.seeding_points}
              onChange={(e) => upd(t.id, "seeding_points", e.target.value)}
              className="bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-3 py-2"
            />
            <div className="flex gap-2">
              <button
                onClick={() => save(t)}
                className="px-3 py-2 rounded-md bg-[#9D4CDD] text-white inline-flex items-center gap-1 text-xs font-display uppercase"
                data-testid={`save-team-${t.id}`}
              >
                <Save className="w-3.5 h-3.5" /> Save
              </button>
              <button
                onClick={() => remove(t.id)}
                className="px-3 py-2 rounded-md bg-red-500/15 border border-red-500/40 text-red-400 inline-flex items-center gap-1 text-xs"
                data-testid={`delete-team-${t.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StandingsTab() {
  const [teams, setTeams] = useState([]);
  useEffect(() => {
    listTeams().then((d) =>
      setTeams([...d].sort((a, b) => b.seeding_points - a.seeding_points))
    );
  }, []);

  const save = async (t) => {
    await updateTeam(t.id, { seeding_points: Number(t.seeding_points) || 0 });
    toast.success(`${t.name} updated`);
  };

  const upd = (id, val) =>
    setTeams(teams.map((t) => (t.id === id ? { ...t, seeding_points: val } : t)));

  return (
    <div className="space-y-2">
      {teams.map((t, i) => (
        <div
          key={t.id}
          className="glass-card rounded-lg p-3 flex items-center gap-4"
          data-testid={`admin-standing-${t.id}`}
        >
          <span
            className={`font-display font-black text-lg w-8 text-center ${
              i < 16 ? "text-[#FF5E00]" : "text-white/40"
            }`}
          >
            {i + 1}
          </span>
          {t.logo && (
            <img src={t.logo} alt="" className="h-8 w-8 rounded bg-[#1E1233]" />
          )}
          <span className="font-display uppercase font-bold flex-1 truncate">
            {t.name}
          </span>
          <input
            type="number"
            value={t.seeding_points}
            onChange={(e) => upd(t.id, e.target.value)}
            className="bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-3 py-2 w-24 text-right"
          />
          <button
            onClick={() => save(t)}
            className="px-3 py-2 rounded-md bg-[#9D4CDD] text-white text-xs font-display uppercase inline-flex items-center gap-1"
          >
            <Save className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

function RulesTab() {
  const [rules, setRules] = useState([]);
  const [active, setActive] = useState(null);

  const refresh = () =>
    listRules().then((d) => {
      setRules(d);
      setActive((cur) => (cur ? d.find((r) => r.id === cur.id) || d[0] : d[0]));
    });
  useEffect(() => {
    refresh();
  }, []);

  const save = async () => {
    if (!active) return;
    await updateRule(active.id, {
      title: active.title,
      content: active.content,
      order: active.order,
    });
    toast.success("Section saved");
    refresh();
  };

  if (!rules.length || !active) {
    return (
      <div className="py-12 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5E00]" />
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-6">
      <div className="glass-card rounded-xl p-3 h-fit">
        {rules.map((r) => (
          <button
            key={r.id}
            onClick={() => setActive(r)}
            className={`w-full text-left px-3 py-2.5 rounded-md font-display uppercase text-xs tracking-wider transition ${
              active.id === r.id
                ? "bg-[#FF5E00] text-black font-bold"
                : "text-white/70 hover:bg-[#9D4CDD]/15 hover:text-white"
            }`}
            data-testid={`admin-rule-tab-${r.slug}`}
          >
            {r.title}
          </button>
        ))}
      </div>
      <div className="glass-card rounded-xl p-6 space-y-4">
        <input
          value={active.title}
          onChange={(e) => setActive({ ...active, title: e.target.value })}
          className="w-full bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-4 py-3 font-display uppercase font-bold text-lg"
          data-testid="admin-rule-title"
        />
        <textarea
          value={active.content}
          onChange={(e) => setActive({ ...active, content: e.target.value })}
          rows={20}
          className="w-full bg-[#0A0512] border border-[#9D4CDD]/40 rounded-md px-4 py-3 font-mono text-sm leading-relaxed"
          data-testid="admin-rule-content"
        />
        <button
          onClick={save}
          className="font-display uppercase tracking-wider text-xs font-bold px-6 py-3 rounded-md bg-[#FF5E00] text-black hover:bg-[#ff7a33] inline-flex items-center gap-2"
          data-testid="admin-rule-save"
        >
          <Save className="w-4 h-4" /> Save Section
        </button>
      </div>
    </div>
  );
}

function BracketTab() {
  const [bracket, setBracket] = useState([]);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    Promise.all([getBracket(), listTeams()]).then(([b, t]) => {
      setBracket(b);
      setTeams(t);
    });
  useEffect(() => {
    refresh();
  }, []);

  const teamMap = {};
  teams.forEach((t) => (teamMap[t.id] = t));

  const generate = async () => {
    setBusy(true);
    try {
      await generateBracket();
      toast.success("Bracket generated from top 16");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("End this bracket? This clears all matches and returns to 'Bracket Not Started'.")) return;
    await resetBracket();
    toast.success("Bracket ended");
    refresh();
  };

  const confirmResult = async (m, score1, score2) => {
    const s1 = Number(score1);
    const s2 = Number(score2);
    if (Number.isNaN(s1) || Number.isNaN(s2) || s1 < 0 || s2 < 0) {
      toast.error("Enter valid scores");
      return;
    }
    if (s1 === s2) {
      toast.error("Scores can't be tied — declare a winner");
      return;
    }
    const winner_id = s1 > s2 ? m.team1_id : m.team2_id;
    // If editing a completed match and the winner is changing, cascade-undo
    // the old winner from later rounds before re-advancing the new one.
    if (m.winner_id && m.winner_id !== winner_id) {
      try {
        await undoMatchWinner(m.id);
      } catch {
        toast.error("Couldn't reverse old winner");
        return;
      }
    }
    await setMatchWinner(m.id, { winner_id, score1: s1, score2: s2 });
    toast.success(`${teamMap[winner_id].name} ${m.winner_id ? "updated" : "advanced"} (${s1}–${s2})`);
    refresh();
  };

  const undoMatch = async (m) => {
    if (!window.confirm("Undo this match? This will also clear any later rounds that depended on this winner.")) return;
    await undoMatchWinner(m.id);
    toast.success("Match reset");
    refresh();
  };

  const ROUND_LABELS = ["", "Round of 16", "Quarterfinals", "Semifinals", "Final"];

  const onDragStart = (e, srcMatch, teamId) => {
    if (!teamId) return;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        srcMatchId: srcMatch.id,
        teamId,
        srcRound: srcMatch.round,
        srcPos: srcMatch.position,
      })
    );
  };

  const isValidDrop = (data, tgtMatch, tgtSlot) => {
    if (!data) return false;
    if (tgtMatch.round !== data.srcRound + 1) return false;
    if (tgtMatch.position !== Math.floor(data.srcPos / 2)) return false;
    const expectedSlot = data.srcPos % 2 === 0 ? "team1" : "team2";
    return expectedSlot === tgtSlot;
  };

  const onDrop = async (e, tgtMatch, tgtSlot) => {
    e.preventDefault();
    let data;
    try {
      data = JSON.parse(e.dataTransfer.getData("application/json"));
    } catch {
      return;
    }
    if (!isValidDrop(data, tgtMatch, tgtSlot)) {
      toast.error("Drop into the matching slot in the next round.");
      return;
    }
    const srcMatch = bracket.find((m) => m.id === data.srcMatchId);
    if (!srcMatch) return;
    // Drag-drop = quick advance with placeholder 1-0 score; admin can undo & re-enter scores via Confirm.
    const score1 = data.teamId === srcMatch.team1_id ? 1 : 0;
    const score2 = data.teamId === srcMatch.team2_id ? 1 : 0;
    try {
      await setMatchWinner(srcMatch.id, { winner_id: data.teamId, score1, score2 });
      toast.success(`${teamMap[data.teamId].name} advanced`);
      refresh();
    } catch {
      toast.error("Failed to advance team");
    }
  };

  const byRound = [1, 2, 3, 4].map((r) =>
    bracket.filter((m) => m.round === r).sort((a, b) => a.position - b.position)
  );

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-xl p-6 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h3 className="font-display uppercase text-lg font-bold">Bracket Controls</h3>
          <p className="text-white/60 text-sm">
            Generate a fresh single-elim bracket from your top 16. Drag a team
            from a match into the next round's slot to advance them.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={generate}
            disabled={busy}
            className="font-display uppercase tracking-wider text-xs font-bold px-5 py-2.5 rounded-md bg-[#FF5E00] text-black hover:bg-[#ff7a33] disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="generate-bracket-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
            Generate Bracket
          </button>
          <button
            onClick={reset}
            className="font-display uppercase tracking-wider text-xs font-bold px-5 py-2.5 rounded-md border border-[#9D4CDD] text-white hover:bg-[#9D4CDD]/15 inline-flex items-center gap-2"
            data-testid="end-bracket-btn"
          >
            <RefreshCw className="w-4 h-4" /> End Bracket
          </button>
        </div>
      </div>

      {bracket.length === 0 ? (
        <div className="glass-card rounded-xl p-10 text-center text-white/60">
          No bracket yet. Click "Generate Bracket" once your top 16 seeding is ready.
        </div>
      ) : (
        <div className="overflow-x-auto pb-6">
          <div className="flex gap-10 min-w-fit items-stretch">
            {byRound.map((matches, idx) => (
              <div
                key={idx + 1}
                className="flex flex-col min-h-full"
                data-testid={`admin-round-col-${idx + 1}`}
              >
                <div className="text-center mb-4">
                  <div className="font-display uppercase text-xs tracking-[0.3em] text-[#FF5E00]">
                    {ROUND_LABELS[idx + 1]}
                  </div>
                  <div className="text-white/40 text-[10px] font-mono">
                    {matches.length} {matches.length === 1 ? "match" : "matches"}
                  </div>
                </div>
                <div className="flex flex-col gap-6 flex-1 justify-around">
                  {matches.map((m) => (
                    <AdminMatchCard
                      key={m.id}
                      match={m}
                      teamMap={teamMap}
                      onDragStart={onDragStart}
                      onDropFn={onDrop}
                      onConfirm={confirmResult}
                      onUndo={undoMatch}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminMatchCard({ match, teamMap, onDragStart, onDropFn, onConfirm, onUndo }) {
  const t1 = teamMap[match.team1_id];
  const t2 = teamMap[match.team2_id];
  const w = match.winner_id;
  const ready = t1 && t2;
  const [s1, setS1] = React.useState(w ? String(match.score1) : "");
  const [s2, setS2] = React.useState(w ? String(match.score2) : "");

  // Re-sync local input state when the underlying match record changes
  // (e.g., after refresh, undo, or successful update).
  React.useEffect(() => {
    setS1(w ? String(match.score1) : "");
    setS2(w ? String(match.score2) : "");
  }, [match.winner_id, match.score1, match.score2, w]);

  const dirty =
    w !== null && w !== undefined
      ? Number(s1) !== match.score1 || Number(s2) !== match.score2
      : s1 !== "" || s2 !== "";

  return (
    <div
      className="glass-card rounded-md overflow-hidden w-60 shadow-[0_4px_20px_rgba(0,0,0,0.4)] hover:border-[#FF5E00] transition-all"
      data-testid={`admin-match-${match.round}-${match.position}`}
    >
      <div className="bg-[#1E1233]/80 px-3 py-1.5 flex items-center justify-between border-b border-[#9D4CDD]/20">
        <span className="font-display uppercase text-[10px] tracking-[0.2em] text-[#9D4CDD]">
          R{match.round} · M{match.position + 1}
        </span>
        {w && (
          <button
            onClick={() => onUndo(match)}
            className="inline-flex items-center gap-1 text-[10px] font-display uppercase tracking-wider text-white/60 hover:text-[#FF5E00]"
            title="Undo this match"
            data-testid={`undo-match-${match.id}`}
          >
            <Undo2 className="w-3 h-3" /> Undo
          </button>
        )}
      </div>
      <div className="divide-y divide-[#9D4CDD]/15">
        <AdminTeamSlot
          match={match}
          slot="team1"
          team={t1}
          isWinner={w === match.team1_id}
          score={w ? match.score1 : null}
          onDragStart={onDragStart}
          onDropFn={onDropFn}
        />
        <AdminTeamSlot
          match={match}
          slot="team2"
          team={t2}
          isWinner={w === match.team2_id}
          score={w ? match.score2 : null}
          onDragStart={onDragStart}
          onDropFn={onDropFn}
        />
      </div>
      {ready && (
        <div className="px-2 py-2 flex items-center gap-1.5 bg-[#0a0512]/40 border-t border-[#9D4CDD]/15">
          <input
            type="number"
            min="0"
            value={s1}
            onChange={(e) => setS1(e.target.value)}
            placeholder="0"
            aria-label={`${t1.name} score`}
            className="w-12 bg-[#0A0512] border border-[#9D4CDD]/40 rounded px-2 py-1 text-center font-mono text-xs focus:outline-none focus:border-[#FF5E00]"
            data-testid={`score1-input-${match.id}`}
          />
          <span className="text-white/30 font-mono text-xs">–</span>
          <input
            type="number"
            min="0"
            value={s2}
            onChange={(e) => setS2(e.target.value)}
            placeholder="0"
            aria-label={`${t2.name} score`}
            className="w-12 bg-[#0A0512] border border-[#9D4CDD]/40 rounded px-2 py-1 text-center font-mono text-xs focus:outline-none focus:border-[#FF5E00]"
            data-testid={`score2-input-${match.id}`}
          />
          <button
            onClick={() => onConfirm(match, s1, s2)}
            disabled={s1 === "" || s2 === "" || (w && !dirty)}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded bg-[#FF5E00] text-black font-display uppercase tracking-wider text-[10px] font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#ff7a33]"
            data-testid={`confirm-result-${match.id}`}
            title={w ? "Update scores" : "Confirm result"}
          >
            <Check className="w-3 h-3" /> {w ? "Update" : "Confirm"}
          </button>
        </div>
      )}
    </div>
  );
}

function AdminTeamSlot({ match, slot, team, isWinner, score, onDragStart, onDropFn }) {
  const [over, setOver] = React.useState(false);
  const empty = !team;
  const draggable = !!team && match.round < 4 && !match.winner_id;

  return (
    <div
      draggable={draggable}
      onDragStart={draggable ? (e) => onDragStart(e, match, team.id) : undefined}
      onDragEnter={(e) => {
        if (empty) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragOver={(e) => {
        if (empty) {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        if (empty) onDropFn(e, match, slot);
      }}
      className={`flex items-center justify-between gap-2 px-3 py-2.5 transition-all select-none ${
        isWinner
          ? "bg-[#FF5E00]/20 border-l-2 border-[#FF5E00] text-white"
          : team
          ? `text-white/90 ${draggable ? "cursor-grab active:cursor-grabbing hover:bg-[#9D4CDD]/15" : ""}`
          : over
          ? "bg-[#9D4CDD]/30 border-l-2 border-[#9D4CDD] text-white"
          : "text-white/30 border-l-2 border-dashed border-[#9D4CDD]/20"
      }`}
      data-testid={`slot-${match.round}-${match.position}-${slot}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {team?.logo && (
          <img src={team.logo} alt="" className="h-5 w-5 rounded bg-[#1E1233]" />
        )}
        <span className="font-display uppercase text-xs tracking-wider truncate">
          {team?.name || (over ? "Drop here" : "N/A")}
        </span>
      </div>
      <span className={`font-mono text-xs ${isWinner ? "text-[#FF5E00]" : "text-white/40"}`}>
        {score ?? "—"}
      </span>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("teams");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    adminVerify()
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5E00]" />
      </div>
    );
  }

  if (!authed) return <LoginForm onLoggedIn={() => setAuthed(true)} />;

  const logout = () => {
    setToken(null);
    setAuthed(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5E00]/40 bg-[#FF5E00]/5 text-[#FF5E00] text-xs uppercase tracking-[0.25em] font-display mb-3">
            <Lock className="w-3 h-3" /> Admin
          </div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tight">
            Control <span className="text-gradient-orange">Panel</span>
          </h1>
        </div>
        <button
          onClick={logout}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#9D4CDD]/60 text-white/80 hover:text-white hover:border-[#FF5E00]"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-8 border-b border-[#9D4CDD]/20">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 px-5 py-3 font-display uppercase text-xs tracking-[0.18em] border-b-2 transition ${
              tab === t.key
                ? "border-[#FF5E00] text-[#FF5E00]"
                : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid={`admin-tab-${t.key}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "teams" && <TeamsTab />}
      {tab === "standings" && <StandingsTab />}
      {tab === "rules" && <RulesTab />}
      {tab === "bracket" && <BracketTab />}
    </div>
  );
}
